/**
 * Admin: Pronunciation Dictionaries library page.
 *
 * Shared W3C PLS (.pls) dictionaries personas can attach in their voice
 * settings, synced to ElevenLabs per API key (spec 2026-07-29).
 */

import { PageFrame } from '@/components/layout/PageFrame'
import { PageHeader } from '@/components/layout/PageHeader'
import { useState, type FormEvent } from 'react'
import {
  Alert, AlertDescription, Badge, Button, Input,
  Modal, ModalBody, ModalContent, ModalDescription, ModalFooter, ModalHeader, ModalTitle,
  Spinner,
} from '@/components/ui'
import { BookOpen, Plus, Trash2, Upload } from 'lucide-react'
import { type LibraryDictionary } from '@/services/pronunciationLibrary'
import {
  useArchivePronunciationDictionary,
  usePronunciationDictionaries,
  useUpdatePronunciationDictionary,
  useUploadPronunciationDictionary,
} from '@/hooks/usePronunciationDictionaries'

export function PronunciationDictionariesPage() {
  const [error, setError] = useState<string | null>(null)
  const [showUpload, setShowUpload] = useState(false)
  const [editTarget, setEditTarget] = useState<LibraryDictionary | null>(null)

  const query = usePronunciationDictionaries()
  const dictionaries = query.data ?? []
  const uploadMutation = useUploadPronunciationDictionary()
  const updateMutation = useUpdatePronunciationDictionary()
  const archiveMutation = useArchivePronunciationDictionary()
  const busy = uploadMutation.isPending || updateMutation.isPending

  const listError = query.isError
    ? (query.error instanceof Error ? query.error.message : 'Failed to load dictionaries')
    : null
  const shownError = error ?? listError

  const handleUpload = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const data = new FormData(e.currentTarget)
    const file = data.get('file') as File | null
    const name = String(data.get('name') || '').trim()
    if (!file || !name) return
    setError(null)
    try {
      await uploadMutation.mutateAsync({
        name,
        description: String(data.get('description') || '') || undefined,
        file,
      })
      setShowUpload(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed')
    }
  }

  const handleReplace = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!editTarget) return
    const data = new FormData(e.currentTarget)
    const file = data.get('file') as File | null
    const name = String(data.get('name') || '').trim()
    setError(null)
    try {
      await updateMutation.mutateAsync({
        id: editTarget.id,
        name: name && name !== editTarget.name ? name : undefined,
        file: file && file.size > 0 ? file : undefined,
      })
      setEditTarget(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Update failed')
    }
  }

  const handleArchive = async (d: LibraryDictionary) => {
    if (!window.confirm(`Archive "${d.name}"? Personas referencing it will fall back to no library pronunciation.`)) return
    setError(null)
    try {
      await archiveMutation.mutateAsync(d.id)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Archive failed')
    }
  }

  return (
    <PageFrame width="list" className="space-y-6">
      <PageHeader
        title="Pronunciation Dictionaries"
        subtitle="Shared .pls dictionaries personas can attach in their voice settings. Uploaded copies are synced to ElevenLabs automatically per API key."
        actions={
          <Button onClick={() => setShowUpload(true)} leftIcon={<Plus className="w-4 h-4" />}>
            Upload Dictionary
          </Button>
        }
      />

      {shownError && <Alert variant="destructive"><AlertDescription>{shownError}</AlertDescription></Alert>}

      {query.isLoading ? (
        <div className="flex items-center justify-center py-12"><Spinner size="lg" /></div>
      ) : query.isError ? null : dictionaries.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <BookOpen className="w-8 h-8 mx-auto mb-2" />
          No dictionaries yet. Upload a W3C PLS (.pls) file to get started.
        </div>
      ) : (
        <div className="space-y-2">
          {dictionaries.map((d) => (
            <div key={d.id} className="flex items-center justify-between rounded-md border border-border p-3">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">{d.name}</span>
                  <Badge variant="secondary">{d.rules_count} rules</Badge>
                  {d.has_phonemes && (
                    <Badge variant="outline">phoneme rules — only apply on Flash v2 / v3 models</Badge>
                  )}
                </div>
                {d.description && <p className="text-sm text-muted-foreground">{d.description}</p>}
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setEditTarget(d)}
                        leftIcon={<Upload className="w-4 h-4" />}>Replace / Rename</Button>
                <Button variant="ghost" size="sm" onClick={() => handleArchive(d)}
                        leftIcon={<Trash2 className="w-4 h-4" />}>Archive</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={showUpload} onOpenChange={setShowUpload}>
        <ModalContent size="sm">
          <ModalHeader>
            <ModalTitle>Upload Pronunciation Dictionary</ModalTitle>
            <ModalDescription>W3C PLS (.pls) file, max 500 KB.</ModalDescription>
          </ModalHeader>
          <form onSubmit={handleUpload}>
            <ModalBody className="space-y-3">
              <Input name="name" placeholder="Name (e.g. Aviation)" required />
              <Input name="description" placeholder="Description (optional)" />
              <input name="file" type="file" accept=".pls,.xml" required className="text-sm" />
            </ModalBody>
            <ModalFooter>
              <Button type="submit" disabled={busy}>{busy ? 'Uploading…' : 'Upload'}</Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>

      <Modal open={editTarget !== null} onOpenChange={(open) => !open && setEditTarget(null)}>
        <ModalContent size="sm">
          <ModalHeader>
            <ModalTitle>Replace / Rename</ModalTitle>
            <ModalDescription>
              Replacing the file updates every persona using this dictionary on their next voice session.
            </ModalDescription>
          </ModalHeader>
          <form onSubmit={handleReplace}>
            <ModalBody className="space-y-3">
              <Input name="name" defaultValue={editTarget?.name} />
              <input name="file" type="file" accept=".pls,.xml" className="text-sm" />
            </ModalBody>
            <ModalFooter>
              <Button type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save'}</Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    </PageFrame>
  )
}
